import React, { useMemo, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { Printer, Download, Minus, Plus } from "lucide-react";

// Try to use the public site URL from env; fall back to the current origin.
function useOrigin() {
  return useMemo(() => {
    // Prefer the deployed origin (custom domain) if configured on the frontend
    if (typeof window !== "undefined") {
      // Use current origin — QR sits under the site the guest is scanning towards
      return window.location.origin;
    }
    return "";
  }, []);
}

export default function TableCodesPanel() {
  const [count, setCount] = useState(12);
  const origin = useOrigin();

  const tables = useMemo(
    () => Array.from({ length: Math.max(1, Math.min(count, 100)) }, (_, i) => i + 1),
    [count]
  );

  const printSheet = () => {
    // Add a print-only stylesheet, then invoke print, then remove
    const styleId = "table-qr-print-style";
    const existing = document.getElementById(styleId);
    if (existing) existing.remove();
    const style = document.createElement("style");
    style.id = styleId;
    // Use textContent (not innerHTML) — CSS inside a <style> tag is safer this way
    style.textContent = `
      @page { size: A4; margin: 12mm; }
      @media print {
        body * { visibility: hidden !important; }
        #table-qr-print-area, #table-qr-print-area * { visibility: visible !important; }
        #table-qr-print-area {
          position: absolute; left: 0; top: 0; width: 100%;
          padding: 0; background: white !important; color: black !important;
          display: grid !important; grid-template-columns: repeat(3, 1fr) !important;
          gap: 8mm !important;
        }
        #table-qr-print-area .qr-card {
          break-inside: avoid; page-break-inside: avoid;
          background: white !important;
          border: 1.5pt solid #B7853D !important;
          padding: 8mm !important;
          text-align: center !important;
        }
        #table-qr-print-area .qr-card * { color: black !important; }
        #table-qr-print-area .qr-card .font-display { color: #B7853D !important; font-size: 20pt !important; }
        #table-qr-print-area .no-print { display: none !important; }
        #table-qr-print-area .qr-card svg { width: 55mm !important; height: 55mm !important; }
      }
    `;
    document.head.appendChild(style);
    window.print();
    setTimeout(() => style.remove(), 500);
  };

  const downloadOne = (n) => {
    const svg = document.getElementById(`qr-svg-${n}`);
    if (!svg) return;
    const serializer = new XMLSerializer();
    const source = serializer.serializeToString(svg);
    // Rasterise via canvas for a PNG download
    const img = new Image();
    const svgBlob = new Blob([source], { type: "image/svg+xml;charset=utf-8" });
    const url = URL.createObjectURL(svgBlob);
    img.onload = () => {
      const size = 800;
      const canvas = document.createElement("canvas");
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, size, size);
      ctx.drawImage(img, 0, 0, size, size);
      URL.revokeObjectURL(url);
      const link = document.createElement("a");
      link.download = `mamamina-table-${n}.png`;
      link.href = canvas.toDataURL("image/png");
      link.click();
    };
    img.src = url;
  };

  return (
    <div className="space-y-6">
      <div className="p-5 border border-[var(--mm-line)] bg-[var(--mm-bg-alt)] flex flex-col md:flex-row md:items-center gap-4">
        <div className="flex-1">
          <div className="section-label mb-2">Table QR Codes</div>
          <p className="text-sm text-[var(--mm-cream-muted)]">
            One QR per table (guests seated → &ldquo;Seated at Table X&rdquo; ribbon,
            Dine-In pre-filled), plus a <span className="text-[var(--mm-accent)] font-semibold">Front-Door QR</span> for
            walk-in pre-orders — customers pick their arrival time and the kitchen holds until they sit down.
          </p>
        </div>

        <div className="flex items-center gap-3">
          <div className="inline-flex items-center gap-2 border border-[var(--mm-line)]">
            <button
              onClick={() => setCount((c) => Math.max(1, c - 1))}
              className="px-3 py-2 text-[var(--mm-cream)] hover:bg-[var(--mm-line)]"
              data-testid="tables-decrease"
            >
              <Minus size={14} />
            </button>
            <input
              type="number"
              value={count}
              onChange={(e) => setCount(parseInt(e.target.value || "1", 10))}
              className="w-14 bg-transparent text-[var(--mm-cream)] text-center py-2 focus:outline-none"
              data-testid="tables-count-input"
              min="1"
              max="100"
            />
            <button
              onClick={() => setCount((c) => Math.min(100, c + 1))}
              className="px-3 py-2 text-[var(--mm-cream)] hover:bg-[var(--mm-line)]"
              data-testid="tables-increase"
            >
              <Plus size={14} />
            </button>
          </div>
          <button
            onClick={printSheet}
            className="btn-primary inline-flex items-center gap-2"
            data-testid="tables-print-sheet"
          >
            <Printer size={14} /> Print Sheet
          </button>
        </div>
      </div>

      <div id="table-qr-print-area" className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
        {/* Door / Pre-Order QR — customer scans on the way in, orders before
            sitting down. Kitchen ticket says "PRE-ORDER · arrives in ~30min". */}
        <div
          key="door"
          className="qr-card bg-[var(--mm-accent)]/10 border-2 border-[var(--mm-accent)] p-5 flex flex-col items-center text-center"
          data-testid="table-qr-card-door"
        >
          <div className="text-[0.62rem] tracking-[0.3em] uppercase text-[var(--mm-accent)] mb-2 font-semibold">
            Front Door · Pre-Order
          </div>
          <div className="font-display italic text-3xl text-[var(--mm-accent)] mb-3">
            Pre-Order
          </div>
          <div className="bg-white p-2 rounded">
            <QRCodeSVG
              id="qr-svg-door"
              value={`${origin}/?table=door`}
              size={180}
              level="H"
              includeMargin={false}
              fgColor="#0e0e0e"
              bgColor="#ffffff"
            />
          </div>
          <div className="text-[0.6rem] tracking-[0.2em] uppercase text-[var(--mm-cream-muted)] mt-3">
            Order before sitting
          </div>
          <div className="text-[0.55rem] font-mono text-[var(--mm-cream-muted)] opacity-70 mt-1 break-all">
            {`${origin}/?table=door`}
          </div>
          <button
            onClick={() => downloadOne("door")}
            className="mt-3 no-print inline-flex items-center gap-1 text-[0.6rem] tracking-[0.2em] uppercase text-[var(--mm-cream-muted)] hover:text-[var(--mm-accent)]"
            data-testid="table-qr-download-door"
          >
            <Download size={11} /> PNG
          </button>
        </div>
        {tables.map((n) => {
          const url = `${origin}/?table=${n}`;
          return (
          <div key={n} className="qr-card bg-[var(--mm-bg-alt)] border border-[var(--mm-line)] p-5 flex flex-col items-center text-center">
              <div className="text-[0.62rem] tracking-[0.3em] uppercase text-[var(--mm-cream-muted)] mb-2">
                Mamamina&apos;s Soulfeed
              </div>
              <div className="font-display italic text-3xl text-[var(--mm-accent)] mb-3">
                Table {n}
              </div>
              <div className="bg-white p-2 rounded">
                <QRCodeSVG
                  id={`qr-svg-${n}`}
                  value={url}
                  size={180}
                  level="H"
                  includeMargin={false}
                  fgColor="#0e0e0e"
                  bgColor="#ffffff"
                />
              </div>
              <div className="text-[0.6rem] tracking-[0.2em] uppercase text-[var(--mm-cream-muted)] mt-3">
                Scan to order
              </div>
              <div className="text-[0.55rem] font-mono text-[var(--mm-cream-muted)] opacity-70 mt-1 break-all">
                {url}
              </div>
              <button
                onClick={() => downloadOne(n)}
                className="mt-3 no-print inline-flex items-center gap-1 text-[0.6rem] tracking-[0.2em] uppercase text-[var(--mm-cream-muted)] hover:text-[var(--mm-accent)]"
                data-testid={`table-qr-download-${n}`}
              >
                <Download size={11} /> PNG
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
